import { getIsTob } from '../../components/DepthOfMarket/helpers'
import { formatResubmitDate } from '../../components/DropdownUpload/helpers'
import { OrderEntryModel } from '../depthOfMarket/types'
import { SecurityOrderData } from '../securities/reducer'
import { QuoteReliability } from '../securities/types'
import {
  CreateTempAggressorOrderAction,
  CreateTempOrderAction
} from './actions'
import {
  Order,
  OrderResponse,
  OrderStatus,
  OrderType,
  TobParameters
} from './types'
import { getOptionalDate } from '../helpers'
import dayjs from 'dayjs'

export const getOrderTypeForResponse = (orderType: OrderType) =>
  orderType === 'buy' ? 0 : 1

export const getOrderTypeFromResponse = (
  orderResponseType: OrderResponse['type']
) => (orderResponseType === 0 ? 'buy' : 'sell')

export const getOrderStatusForResponse = (orderStatus: OrderStatus) =>
  orderStatus === 'pending' ? 0 : orderStatus === 'rejected' ? 1 : 2

export const getOrderStatusFromResponse = (
  orderResponseStatus: OrderResponse['status']
): OrderStatus =>
  orderResponseStatus === 0
    ? 'pending'
    : orderResponseStatus === 1
    ? 'rejected'
    : orderResponseStatus === 2
    ? 'accepted'
    : orderResponseStatus === 3
    ? 'waitingForConfirmation'
    : orderResponseStatus === 4
    ? 'cancelled'
    : orderResponseStatus === 5
    ? 'error'
    : 'notAvailable'

export const createOrdersFromResubmitOrders = (response: any) => {
  for (const dateStr in response) {
    if (response.hasOwnProperty(dateStr)) {
      const secIds = response[dateStr]
      for (const secId in secIds) {
        if (secIds.hasOwnProperty(secId)) {
          const sides = secIds[secId]
          for (const side in sides) {
            if (sides.hasOwnProperty(side)) {
              sides[side] = createOrderFromResponse(sides[side])
            }
          }
        }
      }
    }
  }
  return response
}

export const createOrderFromResponse = (
  orderResponse: OrderResponse,
  pendingOrders?: Order[]
): Order => {
  const orderId = (): string => {
    if (orderResponse.id && orderResponse.id !== '') {
      return orderResponse.id
    }
    if (pendingOrders && pendingOrders.length) {
      for (const order of pendingOrders) {
        if (order.transactionId === orderResponse.transactionId) {
          return orderResponse.id
        }
      }
    }
    return ''
  }
  return {
    ...orderResponse,
    id: orderId(),
    goodTill: goodTillFromServerTime(orderResponse.goodTill),
    spread: orderResponse.spread || undefined,
    type: getOrderTypeFromResponse(orderResponse?.type),
    status: getOrderStatusFromResponse(orderResponse?.status),
    submitTime: new Date(orderResponse.submitTime),
    tradeTime: getOptionalDate(orderResponse.tradeTime),
    expiration: getOptionalDate(orderResponse.expiration),
    updateTime: getOptionalDate(orderResponse.updateTime),
    aggressorOrder: orderResponse.aggressorOrder && {
      ...orderResponse.aggressorOrder,
      submitTime: new Date(orderResponse.aggressorOrder.submitTime),
      expiration: new Date(orderResponse.aggressorOrder.expiration),
      status: getOrderStatusFromResponse(orderResponse.aggressorOrder.status)
    },
    allOrNone: orderResponse.allOrNone,
    individualMin: orderResponse.individualMin,
    custId: orderResponse.custId,
    displayName: orderResponse.displayName
  }
}

export const createResponseFromOrder = (order: Order): OrderResponse => ({
  ...order,
  spread: order.spread || undefined,
  type: getOrderTypeForResponse(order.type),
  status: getOrderStatusForResponse(order.status),
  submitTime: order.submitTime.toISOString(),
  tradeTime: order.tradeTime?.toISOString(),
  expiration: order.expiration && order.expiration.toISOString(),
  updateTime: order.updateTime?.toISOString(),
  aggressorOrder: order.aggressorOrder && {
    ...order.aggressorOrder,
    submitTime: order.aggressorOrder.submitTime.toISOString(),
    expiration: order.aggressorOrder.expiration.toISOString(),
    status: getOrderStatusForResponse(order.aggressorOrder.status)
  }
})

export type ResubmitOrders = Record<
  string,
  Record<number, Record<OrderType, Order>>
>

// Stable key: real id when server-assigned, transactionId when synthetic.
export const getUserOrderKey = (order: Order) =>
  order.id !== '' ? order.id : `tx:${order.transactionId}`

export const isActiveUserOrder = (order: Order) =>
  order.id !== '' &&
  (order.status === 'pending' || order.status === 'waitingForConfirmation')

export const isTrackedTerminalOrder = (order: Order) =>
  order.id !== '' &&
  (order.status === 'accepted' ||
    order.status === 'rejected' ||
    (order.status === 'cancelled' && !!order.initialOrder))

/* Stream echoes resend orders verbatim; treating clone-equal payloads as
   unchanged keeps the records referentially stable, so selectors and their
   subscribers skip recomputes/re-renders on no-op batches. */
const isSameOrder = (stored: Order | undefined, incoming: Order) =>
  stored !== undefined &&
  (stored === incoming || JSON.stringify(stored) === JSON.stringify(incoming))

export const upsertUserOrders = (
  userOrders: Record<string, Order>,
  resubmitOrders: ResubmitOrders,
  orders: Order[]
) => {
  /* Writes go to an up-front clone; the originals are returned untouched
     unless a writable*() call swaps the corresponding next* to its clone. */
  const clonedUserOrders = { ...userOrders }
  let nextUserOrders = userOrders
  const writableUserOrders = () => {
    nextUserOrders = clonedUserOrders
    return clonedUserOrders
  }
  const clonedResubmitOrders = { ...resubmitOrders }
  let nextResubmitOrders = resubmitOrders
  const writableResubmitOrders = () => {
    nextResubmitOrders = clonedResubmitOrders
    return clonedResubmitOrders
  }
  for (const order of orders) {
    if (
      order.id !== '' &&
      order.transactionId !== undefined &&
      clonedUserOrders[`tx:${order.transactionId}`] !== undefined
    ) {
      // server echo of a synthetic order: the id-keyed record replaces it
      delete writableUserOrders()[`tx:${order.transactionId}`]
    }
    const key = getUserOrderKey(order)
    if (!isSameOrder(clonedUserOrders[key], order)) {
      writableUserOrders()[key] = order
    }
    if (order.id !== '' && !order.initialOrder) {
      // passive order: keep the latest per date/security/side for resubmit
      const dateStr = formatResubmitDate(order.submitTime)
      const daySecurity = clonedResubmitOrders[dateStr]?.[order.securityId]
      const existing = daySecurity?.[order.type]
      if (
        (!existing || order.submitTime >= existing.submitTime) &&
        !isSameOrder(existing, order)
      ) {
        writableResubmitOrders()[dateStr] = {
          ...clonedResubmitOrders[dateStr],
          [order.securityId]: {
            ...daySecurity,
            [order.type]: order
          }
        }
      }
    }
  }
  return { userOrders: nextUserOrders, resubmitOrders: nextResubmitOrders }
}

export const applyFakeTransactionId = (
  userOrders: Record<string, Order>,
  fakeTransactionId: number,
  transactionId: number
) => {
  const syntheticKey = `tx:${fakeTransactionId}`
  const synthetic: Order | undefined = userOrders[syntheticKey]
  if (!synthetic) {
    return userOrders
  }
  const nextUserOrders = { ...userOrders }
  delete nextUserOrders[syntheticKey]
  const serverOrderArrived = Object.values(nextUserOrders).some(
    (order) => order.transactionId === transactionId
  )
  if (!serverOrderArrived) {
    // keep the synthetic under its real transaction id until the server echoes it
    nextUserOrders[`tx:${transactionId}`] = { ...synthetic, transactionId }
  }
  return nextUserOrders
}

export const addOrUpdateNewOperatorOrders = (
  stateOperatorOrders: Record<string, Order>,
  newOrders: Order[]
) => {
  /* Writes go to an up-front clone; the original is returned untouched
     unless a writableOperatorOrders() call swaps nextOperatorOrders to it. */
  const clonedOperatorOrders = { ...stateOperatorOrders }
  let nextOperatorOrders = stateOperatorOrders
  const writableOperatorOrders = () => {
    nextOperatorOrders = clonedOperatorOrders
    return clonedOperatorOrders
  }
  for (const order of newOrders) {
    if (!isSameOrder(clonedOperatorOrders[order.id], order)) {
      writableOperatorOrders()[order.id] = order
    }
  }
  return nextOperatorOrders
}

export const buildTempAggressorOrder = (
  payload: CreateTempAggressorOrderAction['payload']
): Order => {
  return {
    transactionId: payload.transactionId,
    id: '',
    securityId: payload.initialOrder.securityId,
    type: payload.initialOrder.type === 'buy' ? 'sell' : ('buy' as OrderType),
    displayPrice: payload.initialOrder.price.toString(),
    price: payload.initialOrder.price,
    markedPrice: payload.initialOrder.markedPrice,
    size: payload.initialOrder.size,
    isSpreadOrder: payload.initialOrder.isSpreadOrder,
    status: 'creationPending' as OrderStatus,
    submitTime: new Date(),
    canAggress: false,
    qr: payload.initialOrder.qr,
    initialOrder: payload.initialOrder,
    myFirm: true,
    allOrNone: false,
    custId: payload.initialOrder.custId,
    corpOrderId: payload.initialOrder.corpOrderId,
    displayName: payload.initialOrder.displayName,
    individualMin: payload.initialOrder.individualMin,
    tob: payload.initialOrder.tob,
    totalSize: payload.initialOrder.totalSize,
    isRFQEligible: false
  }
}

export const buildTempOrder = (
  payload: CreateTempOrderAction['payload']
): Order => {
  return {
    transactionId: payload.transactionId,
    id: '',
    securityId: payload.securityId,
    type: payload.type,
    price: payload.isSpread ? 0 : payload.price,
    markedPrice: payload.isSpread ? 0 : payload.price,
    displayPrice: payload.price.toString(),
    spread: payload.isSpread ? payload.price : undefined,
    size: payload.size,
    isSpreadOrder: payload.isSpread,
    status: 'creationPending' as OrderStatus,
    submitTime: new Date(),
    canAggress: false,
    qr: QuoteReliability.All,
    initialOrder: payload.initialOrder,
    myFirm: true,
    allOrNone: payload.allOrNone,
    custId: 0,
    corpOrderId: null,
    displayName: null,
    individualMin: payload.individualMin,
    tob: payload.tob,
    totalSize: payload.totalSize ?? 0,
    isRFQEligible: false
  }
}

export const updateFakeTransactionIdMap = (
  fakeTransactionIdMap: Record<number, number>,
  fakeTransactionId: number,
  transactionId: number
) => ({ ...fakeTransactionIdMap, [fakeTransactionId]: transactionId })

export const updateHitLiftErrors = (
  hitLiftErrors: Record<number, string>,
  transactionId: number,
  error: string
) => ({ ...hitLiftErrors, [transactionId]: error })

export const showBest = (security: SecurityOrderData) => {
  let formattedString = ''
  if (security.bestBid) {
    formattedString += 'Best Bid: '
    formattedString +=
      'id: ' +
      security.bestBid.id +
      ', price: ' +
      security.bestBid.price +
      ', size: ' +
      security.bestBid.size
  }
  if (security.bestOffer) {
    formattedString += 'Best Offer: '
    formattedString +=
      'id: ' +
      security.bestOffer.id +
      ', price: ' +
      security.bestOffer.price +
      ', ' +
      'size: ' +
      security.bestOffer.size
  }
  return formattedString
}
export const nullTob = { tob: false, floorPrice: 0, limitPrice: 0 }

const timeInputFormat = 'HH:mm'
export const goodTillToServerTime = (goodTill?: string) => {
  if (!goodTill) return goodTill
  const now = dayjs()
  const todayStr = now.tz('America/New_York').format('YYYY-MM-DD')

  const goodTillDate = dayjs(`${todayStr} ${goodTill}`).tz('America/New_York')
  return goodTillDate.format(timeInputFormat)
}

export const goodTillFromServerTime = (goodTill?: string) => {
  if (!goodTill) return goodTill
  const tz = dayjs.tz.guess()
  const now = dayjs().tz(tz)
  // sort of, it's buggy
  const nowNy = now.tz('America/New_York')
  // make a dayjs date that's what we thought we had
  const realNowNy = dayjs().hour(nowNy.hour()).minute(nowNy.minute())
  // find out how far apart they are so we can convert
  const timeDifference = now.diff(realNowNy.toDate())
  const [hours, minutes] = goodTill.split(':')
  // correct for 24 hour clock since there's no am/pm setter
  // goodTill in local timezone (but it's coming from the server in ET)
  const uncorrectedGoodTill = dayjs()
    .hour(Number(hours))
    .minute(Number(minutes))
    .second(0)
  // correct time and return
  return uncorrectedGoodTill.add(timeDifference, 'ms').format(timeInputFormat)
}

export function orderModelToParams(model: OrderEntryModel) {
  // for some applications, we "collapse" spread and price into the same value
  const isSpreadOrder = model.amountType === 'spread'
  const isTob = getIsTob(model.amount)
  const onePrice = isTob
    ? (model.amount as TobParameters).limitPrice || undefined
    : parseFloat(model.amount as string)
  // here they are separated out for applications that need that
  const actualPrice = !isSpreadOrder ? onePrice : undefined
  const spread = isSpreadOrder ? onePrice : undefined
  /*  When we created iceberg orders, we already had size on both
      the front and the back end.
      We added totalSize to Orders and StagedOrders on the front end,
      leaving size to be displaySize except when we don't have
      a displaySize, in which case totalSize from the model goes into size.
      On the back end, we added displaySize, which means that size there = totalSize
      from the OEM.
   */
  const clientSize = model.displaySize
    ? parseInt(model.displaySize, 10)
    : parseInt(model.totalSize, 10)
  const serverSize = parseInt(model.totalSize, 10)
  const displaySize = parseInt(model.displaySize, 10) || undefined

  const tob = isTob ? (model.amount as TobParameters) : nullTob
  const individualMin = parseInt(model.minSize || '0', 10)
  const goodTill = goodTillToServerTime(model.goodTill)
  return {
    onePrice,
    actualPrice,
    spread,
    isSpreadOrder,
    clientSize,
    serverSize,
    totalSize: serverSize,
    displaySize,
    individualMin,
    tob,
    goodTill
  }
}
